import { ReactElement, useCallback, memo, KeyboardEventHandler } from 'react';
import classnames from 'classnames';

import { keyboardKeys, keyboardMatch } from '@hh.ru/magritte-common-keyboard';
import { DotFilledSize24, ChevronRightOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { Action } from '@hh.ru/magritte-ui-tree-selector/Action';
import { TreeSelectorItemBase } from '@hh.ru/magritte-ui-tree-selector/TreeSelectorItemBase';
import { AdditionalDefault, TreeModel } from '@hh.ru/magritte-ui-tree-selector/collection/types';
import { RenderItem } from '@hh.ru/magritte-ui-tree-selector/types';
import { Text } from '@hh.ru/magritte-ui-typography';

import styles from './tree-selector-item.less';

interface ItemContent<Additional extends AdditionalDefault> {
    item: TreeModel<Additional>;
    parentId?: TreeModel<Additional>['id'];
    hasChildren: boolean;
    hasAction: boolean;
    letter?: string;
    onExpansion: (id: string) => void;
    isExpanded: boolean;
    isSelected: boolean;
    isDisabled: boolean;
    onChange: (id: string, isSelected: boolean) => void;
    isIndeterminate: boolean;
    singleChoice?: boolean;
    hasLetterOnLevel: boolean;
    hasDot: boolean;
    maxControlsOnLevel: number;
    renderItemForDesktop?: RenderItem<Additional>;
    isSearch: boolean;
}

const ItemContentComponent = <Additional extends AdditionalDefault>({
    item,
    parentId,
    hasAction,
    hasChildren,
    letter,
    isExpanded,
    isDisabled,
    onExpansion,
    hasLetterOnLevel,
    isSelected,
    onChange,
    isIndeterminate,
    singleChoice,
    hasDot,
    maxControlsOnLevel,
    isSearch,
    renderItemForDesktop = TreeSelectorItemBase,
}: ItemContent<Additional>): ReactElement => {
    const handleExpandableClick = useCallback(() => onExpansion && onExpansion(item.id), [item.id, onExpansion]);
    const currentActionCount = +hasDot + +hasAction + +hasChildren;
    const neededSpacesCount = maxControlsOnLevel - currentActionCount;

    const handleExpandableKeyDown = useCallback<KeyboardEventHandler>(
        (event) => {
            if (keyboardMatch(event.nativeEvent, keyboardKeys.ArrowLeft)) {
                isExpanded && onExpansion(item.id);
                event.preventDefault();
                return;
            }
            if (keyboardMatch(event.nativeEvent, keyboardKeys.ArrowRight)) {
                !isExpanded && onExpansion(item.id);
                event.preventDefault();
            }
        },
        [isExpanded, onExpansion, item.id]
    );

    const input = hasAction ? (
        <Action
            selected={isSelected}
            onChange={onChange}
            id={item.id}
            onKeyDown={handleExpandableKeyDown}
            indeterminate={isIndeterminate}
            singleChoice={singleChoice}
            name={`tree-selector-item-${parentId || 'top'}-${item.id}`}
        />
    ) : undefined;

    return (
        <div
            className={classnames(styles.wrapper, {
                [styles.wrapperActive]: hasChildren || hasAction,
            })}
            onClick={hasChildren && !hasAction ? handleExpandableClick : undefined}
        >
            {(letter || hasLetterOnLevel) && (
                <div className={styles.letter}>
                    {letter && (
                        <Text typography="subtitle-1-semibold" style="secondary" Element="span">
                            {letter}
                        </Text>
                    )}
                </div>
            )}
            {hasDot && (
                <div className={styles.icon}>
                    <DotFilledSize24 initial="tertiary" />
                </div>
            )}

            {[...Array(neededSpacesCount).keys()].map((_, i) => (
                <div key={i} className={styles.space} />
            ))}

            {hasChildren && (
                <div
                    className={classnames(styles.icon, styles.iconActive, {
                        [styles.iconUp]: isExpanded,
                    })}
                    data-qa={`tree-selector-chevron tree-selector-chevron-${item.id}`}
                    onClick={hasAction ? handleExpandableClick : undefined}
                >
                    <ChevronRightOutlinedSize24 initial="tertiary" />
                </div>
            )}
            {renderItemForDesktop({ disabled: isDisabled, input, item, isSearch, isParent: hasChildren })}
        </div>
    );
};

export const ItemContent = memo(ItemContentComponent) as <Additional extends AdditionalDefault>(
    props: ItemContent<Additional>
) => ReactElement;
